/**
 * components/platform/controls.tsx
 * Form controls + small layout helpers for the platform console, reproducing
 * design/theme.css (.t-btn, .t-input, .t-field, .t-switch, .t-check, .t-modal)
 * and data.jsx (ProductHeader, StatStrip, Stat) exactly, cross-platform.
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  StyleSheet,
  ActivityIndicator,
  Modal as RNModal,
  ScrollView,
  Platform,
} from 'react-native';

import { useTheme } from '../../context/themeContext';
import { FS, FW, R, Tone } from '../../utils/platformTokens';
import Icon, { IconName } from '../icon';
import { useBlockCopyPaste, noCopyPasteProps } from '../../utils/noCopyPaste';

const webInput = Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {};

/* ── PButton ── (.t-btn + variants) ──────────────────────────────────────── */
export type BtnKind = 'default' | 'primary' | 'accent' | 'danger' | 'ghost' | 'outline';
export type BtnSize = 'xs' | 'sm' | 'md' | 'lg';

export const PButton: React.FC<{
  kind?: BtnKind;
  size?: BtnSize;
  icon?: IconName;
  iconOnly?: boolean;
  block?: boolean;
  loading?: boolean;
  disabled?: boolean;
  onPress?: () => void;
  children?: React.ReactNode;
  style?: any;
  textColor?: string;
}> = ({ kind = 'default', size = 'md', icon, iconOnly, block, loading, disabled, onPress, children, style, textColor }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const [hover, setHover] = useState(false);

  const height = size === 'xs' ? 22 : size === 'sm' ? 26 : size === 'lg' ? 38 : 32;
  const padH = size === 'xs' ? 8 : size === 'sm' ? 10 : size === 'lg' ? 18 : 14;
  const fontSize = size === 'xs' ? FS.xs : size === 'sm' ? FS.sm : size === 'lg' ? FS.lg : FS.md;

  let bg = c.card, border = c.line, fg = c.ink;
  if (kind === 'primary') { bg = c.ink; fg = c.paper; border = c.ink; }
  else if (kind === 'accent') { bg = hover ? c.accentInk : c.accent; fg = '#fff'; border = bg; }
  else if (kind === 'danger') { bg = c.danger; fg = '#fff'; border = c.danger; }
  else if (kind === 'ghost') { bg = hover ? c.paper2 : 'transparent'; fg = c.ink2; border = 'transparent'; }
  else if (kind === 'outline') { bg = 'transparent'; fg = c.ink; border = c.line; }
  else { bg = hover ? c.paper2 : c.card; fg = c.ink; border = c.line; }

  return (
    <Pressable
      onPress={disabled || loading ? undefined : onPress}
      onHoverIn={() => setHover(true)}
      onHoverOut={() => setHover(false)}
      style={[
        {
          height, borderRadius: R.r2, borderWidth: 1, borderColor: border, backgroundColor: bg,
          flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
          paddingHorizontal: iconOnly ? 0 : padH, width: iconOnly ? height : block ? '100%' : undefined,
          alignSelf: block ? 'stretch' : 'flex-start', opacity: disabled ? 0.5 : 1,
        },
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator size="small" color={textColor || fg} />
      ) : (
        <>
          {icon && <Icon name={icon} size={size === 'lg' ? 18 : 16} color={textColor || fg} />}
          {!iconOnly && children != null && (
            <Text style={{ color: textColor || fg, fontSize, fontWeight: FW.medium }}>{children}</Text>
          )}
        </>
      )}
    </Pressable>
  );
};

/* ── PField ── (.t-field — label + hint + error wrapper) ─────────────────── */
export const PField: React.FC<{
  label?: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}> = ({ label, hint, error, children }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <View style={{ gap: 4 }}>
      {!!label && <Text style={{ fontSize: FS.sm, color: c.ink2, fontWeight: FW.medium }}>{label}</Text>}
      {children}
      {!!error ? (
        <Text style={{ fontSize: FS.xs, color: c.danger }}>{error}</Text>
      ) : (
        !!hint && <Text style={{ fontSize: FS.xs, color: c.ink3 }}>{hint}</Text>
      )}
    </View>
  );
};

/* ── PTextInput ── (.t-input) ────────────────────────────────────────────── */
export const PTextInput: React.FC<{
  value: string;
  onChangeText: (v: string) => void;
  label?: string;
  hint?: string;
  error?: string;
  placeholder?: string;
  secureTextEntry?: boolean;
  editable?: boolean;
  mono?: boolean;
  leadingIcon?: IconName;
  keyboardType?: 'default' | 'numeric' | 'email-address';
  maxLength?: number;
  align?: 'left' | 'center';
  letterSpacing?: number;
  style?: any;
}> = ({ value, onChangeText, label, hint, error, placeholder, secureTextEntry, editable = true, mono, leadingIcon, keyboardType, maxLength, align, letterSpacing, style }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const [focus, setFocus] = useState(false);
  // Masked fields (passwords) get no clipboard access.
  const inputRef = useBlockCopyPaste<TextInput>(!!secureTextEntry);
  const field = (
    <View style={{ position: 'relative', justifyContent: 'center' }}>
      {leadingIcon && (
        <View style={{ position: 'absolute', left: 10, zIndex: 1 }}>
          <Icon name={leadingIcon} size={16} color={c.ink3} />
        </View>
      )}
      <TextInput
        ref={inputRef}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={c.ink4}
        secureTextEntry={secureTextEntry}
        {...noCopyPasteProps(!!secureTextEntry)}
        editable={editable}
        keyboardType={keyboardType}
        maxLength={maxLength}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={[
          {
            height: 34, borderWidth: 1, borderRadius: R.r2, paddingHorizontal: 12,
            paddingLeft: leadingIcon ? 32 : 12, fontSize: FS.md, color: c.ink,
            backgroundColor: editable ? c.card : c.paper2,
            borderColor: error ? c.danger : focus ? c.accent : c.line,
            textAlign: align === 'center' ? 'center' : 'left',
          },
          mono ? { fontFamily: theme.font.mono } : null,
          letterSpacing != null ? { letterSpacing } : null,
          webInput,
          style,
        ]}
      />
    </View>
  );
  return label || hint || error ? <PField label={label} hint={hint} error={error}>{field}</PField> : field;
};

/* ── PTextarea ── (textarea.t-input) ─────────────────────────────────────── */
export const PTextarea: React.FC<{
  value: string;
  onChangeText: (v: string) => void;
  label?: string;
  placeholder?: string;
  rows?: number;
  editable?: boolean;
}> = ({ value, onChangeText, label, placeholder, rows = 3, editable = true }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const [focus, setFocus] = useState(false);
  const field = (
    <TextInput
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={c.ink4}
      editable={editable}
      multiline
      numberOfLines={rows}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      style={[
        {
          minHeight: 24 * rows, borderWidth: 1, borderRadius: R.r2, padding: 8, paddingHorizontal: 12,
          fontSize: FS.md, color: c.ink, backgroundColor: editable ? c.card : c.paper2,
          borderColor: focus ? c.accent : c.line, textAlignVertical: 'top',
        },
        webInput,
      ]}
    />
  );
  return label ? <PField label={label}>{field}</PField> : field;
};

/* ── PSelect ── (select.t-input — dropdown) ──────────────────────────────── */
export interface Option { value: string; label: string }
export const PSelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: Option[];
  label?: string;
  disabled?: boolean;
  width?: number;
  size?: 'sm' | 'md';
}> = ({ value, onChange, options, label, disabled, width, size = 'md' }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);
  const height = size === 'sm' ? 28 : 34;
  const field = (
    <View style={{ width }}>
      <Pressable
        disabled={disabled}
        onPress={() => setOpen(true)}
        style={{
          height, borderWidth: 1, borderRadius: R.r2, paddingHorizontal: 12, paddingRight: 28,
          backgroundColor: disabled ? c.paper2 : c.card, borderColor: c.line,
          flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <Text style={{ fontSize: size === 'sm' ? FS.sm : FS.md, color: disabled ? c.ink3 : c.ink }} numberOfLines={1}>
          {current?.label ?? ''}
        </Text>
        <View style={{ position: 'absolute', right: 9 }}>
          <Icon name="chev-d" size={14} color={c.ink3} />
        </View>
      </Pressable>
      {open && (
        <RNModal transparent visible animationType="fade" onRequestClose={() => setOpen(false)}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setOpen(false)} />
          <View style={styles.selectSheetWrap} pointerEvents="box-none">
            <View style={[styles.selectSheet, { backgroundColor: c.card, borderColor: c.line }]}>
              <ScrollView>
                {options.map((o) => (
                  <Pressable
                    key={o.value}
                    onPress={() => { onChange(o.value); setOpen(false); }}
                    style={{
                      paddingHorizontal: 12, paddingVertical: 10,
                      backgroundColor: o.value === value ? c.accentSoft : 'transparent',
                    }}
                  >
                    <Text style={{ fontSize: FS.md, color: o.value === value ? c.accentInk : c.ink }}>{o.label}</Text>
                  </Pressable>
                ))}
              </ScrollView>
            </View>
          </View>
        </RNModal>
      )}
    </View>
  );
  return label ? <PField label={label}>{field}</PField> : field;
};

/* ── PCheckbox ── (.t-check) ─────────────────────────────────────────────── */
export const PCheckbox: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label?: string }> = ({
  checked, onChange, label,
}) => {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <Pressable onPress={() => onChange(!checked)} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <View
        style={{
          width: 16, height: 16, borderRadius: 4, borderWidth: 1,
          borderColor: checked ? c.accent : c.lineStrong, backgroundColor: checked ? c.accent : c.card,
          alignItems: 'center', justifyContent: 'center',
        }}
      >
        {checked && <Icon name="check" size={11} color="#fff" />}
      </View>
      {!!label && <Text style={{ fontSize: FS.md, color: c.ink2 }}>{label}</Text>}
    </Pressable>
  );
};

/* ── PSwitch ── (.t-switch) ──────────────────────────────────────────────── */
export const PSwitch: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label?: string }> = ({
  checked, onChange, label,
}) => {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <Pressable onPress={() => onChange(!checked)} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
      <View
        style={{
          width: 32, height: 18, borderRadius: R.full, borderWidth: 1,
          backgroundColor: checked ? c.accent : c.paper3, borderColor: checked ? c.accent : c.lineStrong,
          justifyContent: 'center', paddingHorizontal: 1,
        }}
      >
        <View
          style={{
            width: 14, height: 14, borderRadius: 7, backgroundColor: c.card,
            transform: [{ translateX: checked ? 14 : 0 }],
          }}
        />
      </View>
      {!!label && <Text style={{ fontSize: FS.sm, color: c.ink2 }}>{label}</Text>}
    </Pressable>
  );
};

/* ── PModal ── (.t-modal) ────────────────────────────────────────────────── */
export const PModal: React.FC<{
  open: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'wide';
  footer?: React.ReactNode;
  children: React.ReactNode;
  closeOnBackdrop?: boolean;
}> = ({ open, onClose, title, size = 'md', footer, children, closeOnBackdrop = true }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  if (!open) return null;
  const width = size === 'sm' ? 380 : size === 'wide' ? 760 : 520;
  return (
    <RNModal transparent visible animationType="fade" onRequestClose={onClose}>
      <Pressable
        style={[styles.modalBackdrop]}
        onPress={closeOnBackdrop ? onClose : undefined}
      >
        <Pressable style={[styles.modal, { width, backgroundColor: c.card }]} onPress={() => {}}>
          {!!title && (
            <View style={[styles.modalHead, { borderColor: c.line }]}>
              <Text style={{ fontSize: FS.lg, fontWeight: FW.semibold, color: c.ink }}>{title}</Text>
              <Pressable onPress={onClose} hitSlop={8}><Icon name="x" size={16} color={c.ink3} /></Pressable>
            </View>
          )}
          <ScrollView contentContainerStyle={{ padding: 18 }} style={{ flexGrow: 0 }}>
            {children}
          </ScrollView>
          {!!footer && (
            <View style={[styles.modalFoot, { borderColor: c.line, backgroundColor: c.paper2 }]}>{footer}</View>
          )}
        </Pressable>
      </Pressable>
    </RNModal>
  );
};

/* ── ProductHeader ── (products.jsx) ─────────────────────────────────────── */
export const ProductHeader: React.FC<{ title: string; sub?: string; action?: React.ReactNode }> = ({
  title, sub, action,
}) => {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 16 }}>
      <View style={{ flex: 1 }}>
        <Text style={{ fontSize: FS.xxl, fontWeight: FW.semibold, color: c.ink, letterSpacing: -0.2 }}>{title}</Text>
        {!!sub && <Text style={{ color: c.ink3, fontSize: FS.md, marginTop: 4 }}>{sub}</Text>}
      </View>
      {action}
    </View>
  );
};

/* ── StatStrip / Stat ── */
export const StatStrip: React.FC<{ items: { label: string; value: React.ReactNode; tone?: string }[] }> = ({ items }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
      {items.map((s) => (
        <View key={s.label} style={[styles.stat, { backgroundColor: c.card, borderColor: c.line }]}>
          <Text style={{ fontSize: FS.sm, color: c.ink3, fontWeight: FW.medium }}>{s.label}</Text>
          <Text style={{ fontSize: 22, fontWeight: FW.semibold, marginTop: 4, color: s.tone || c.ink, fontFamily: theme.font.mono }}>
            {s.value}
          </Text>
        </View>
      ))}
    </View>
  );
};

export const Stat: React.FC<{ label: string; value: React.ReactNode; sub?: string; tone?: string }> = ({ label, value, sub, tone }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <View style={[styles.statLg, { backgroundColor: c.card, borderColor: c.line }]}>
      <Text style={{ fontSize: FS.sm, color: c.ink3, fontWeight: FW.medium }}>{label}</Text>
      <Text style={{ fontSize: 28, fontWeight: FW.semibold, marginTop: 6, color: tone || c.ink, fontFamily: theme.font.mono }}>{value}</Text>
      {!!sub && <Text style={{ fontSize: FS.sm, color: c.ink3, marginTop: 2 }}>{sub}</Text>}
    </View>
  );
};

/* ── PEmpty ── (.t-empty) ────────────────────────────────────────────────── */
export const PEmpty: React.FC<{ icon?: IconName; title: string; children?: React.ReactNode; action?: React.ReactNode }> = ({
  icon, title, children, action,
}) => {
  const { theme } = useTheme();
  const c = theme.colors;
  return (
    <View style={[styles.empty, { borderColor: c.lineStrong, backgroundColor: c.paper2 }]}>
      {icon && <Icon name={icon} size={24} color={c.ink3} />}
      <Text style={{ fontWeight: FW.medium, color: c.ink2, marginTop: 8 }}>{title}</Text>
      {!!children && <Text style={{ color: c.ink3, fontSize: FS.md, marginTop: 4, textAlign: 'center' }}>{children}</Text>}
      {!!action && <View style={{ marginTop: 12 }}>{action}</View>}
    </View>
  );
};

const styles = StyleSheet.create({
  selectSheetWrap: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  selectSheet: {
    width: 320, maxWidth: '100%', maxHeight: 360, borderWidth: 1, borderRadius: R.r3, overflow: 'hidden',
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 16px 32px -12px rgba(16,24,40,0.30)' } as any) : { elevation: 8 }),
  },
  modalBackdrop: { flex: 1, backgroundColor: 'rgba(16,24,40,0.42)', alignItems: 'center', justifyContent: 'center', padding: 24 },
  modal: {
    maxWidth: '100%', maxHeight: '90%', borderRadius: R.r4, overflow: 'hidden',
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 16px 32px -12px rgba(16,24,40,0.30)' } as any) : { elevation: 12 }),
  },
  modalHead: { paddingHorizontal: 18, paddingVertical: 14, borderBottomWidth: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  modalFoot: { paddingHorizontal: 18, paddingVertical: 12, borderTopWidth: 1, flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 8 },
  stat: { flexGrow: 1, minWidth: 150, borderWidth: 1, borderRadius: R.r3, paddingVertical: 14, paddingHorizontal: 16 },
  statLg: { flexGrow: 1, minWidth: 200, borderWidth: 1, borderRadius: R.r3, padding: 18 },
  empty: { borderWidth: 1, borderStyle: 'dashed', borderRadius: R.r3, padding: 32, alignItems: 'center' },
});
